import { Box, InputAdornment, Typography } from "@mui/material";
import toast, { Toaster } from "react-hot-toast";
import { groupingStyles } from "../ExpenseRequest/ExpenseRequest";
import {
  CommonButton,
  CustomRequiredInputLabel,
  CustomTextFeild,
} from "../../common/StyledComponents/StyledFields";
import { useFormik } from "formik";
import { useAppDispatch, useAppSelector } from "../../../app/hook";
import {
  addExpenseCategories,
  addExpenseCategoryAccountNumbers,
} from "../../../features/epxenseRequestSlice";
import ErrorPopup from "../../common/ErrorPopup";
import {
  apiRequest,
  catchErrorMessageHandler,
} from "../../../infrastructure/backendService";
import apiPath from "../../../config/apiPaths.json";
import constants from "../../../config/constants.json";
import CustomSelectField from "../../common/CustomSelectField/CustomSelectField";
import { useEffect, useState } from "react";
import { AttachMoney } from "@mui/icons-material";
import { textFieldStyles } from "../ExpenseRequest/CreateExpenseRequest/CreateExpenseRequest";
import { number, object, string } from "yup";
import ButtonLoader from "../../common/ButtonLoader/ButtonLoader";
import SuccessPopup from "../../devotee/donation/successPopup";
import CreateSubAccount from "../SubAccounts/CreateSubAccount";

const AddOrUpdateBudget = () => {
  const dispatch = useAppDispatch();
  const { expenseCategories, expenseCategoryAccountNumbers } = useAppSelector(
    (state) => state.expenseRequest
  );

  const [btnLoading, setBtnLoading] = useState(false);
  const [openAddSubAccountDialog, setOpenAddSubAccountDialog] = useState(false);

  const getCurrentYearAndNextYears = () => {
    const currentYear = new Date().getFullYear();
    const yearsArray = [];

    for (let i = 0; i <= 5; i++) {
      yearsArray.push({ year: currentYear + i });
    }

    return yearsArray;
  };

  const initialValues = {
    account: "",
    subAccount: "",
    year: new Date().getFullYear(),
    budgetAmount: "",
    subAcId: "",
    showSubAccountField: false,
    showYearsField: false,
  };

  const validationSchema = object().shape({
    subAccount: string().required("Please select the sub-account"),
    budgetAmount: number().required("Amount is required"),
    // .min(1, "Minimum amount must be $1"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit(values) {
      setBtnLoading(true);
      if (values.subAcId) {
        handleUpdateBudget(values);
      } else {
        handleAddBudget(values);
      }
    },
    enableReinitialize: true,
  });

  const getExpenseCategories = () => {
    apiRequest({
      method: "GET",
      endpoint: apiPath.getAllExpenseAccounts,
      includeToken: true,
      params: {
        page: 0,
        size: 300,
        direction: "asc",
        sortBy: "description",
      },
    })
      .then((res) => {
        dispatch(addExpenseCategories(res?.data?.content));
      })
      .catch((err) => {
        console.log(err);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
      });
  };

  const getExpenseCategoryAccounts = (categoryAccountId: string) => {
    apiRequest({
      method: "GET",
      endpoint: apiPath.getAllSubAccounts,
      includeToken: true,
      params: {
        page: 0,
        size: 500,
        direction: "asc",
        sortBy: "description",
        accountId: categoryAccountId,
      },
    })
      .then((res) => {
        dispatch(addExpenseCategoryAccountNumbers(res?.data?.content));
        formik.setFieldValue("showSubAccountField", true);
      })
      .catch((err) => {
        console.log(err);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
      });
  };

  const getBudgetDetailsByYear = () => {
    const { values } = formik;
    apiRequest({
      method: "GET",
      endpoint: apiPath.expenseDetailBudget
        ?.replace("{accountId}", values?.account)
        ?.replace("{subAccountId}", values?.subAccount),
      includeToken: true,
      params: {
        year: formik.values.year,
      },
    })
      .then((res) => {
        const { subAccountBudgetAmount, subAcId } = res?.data;
        formik.setFieldValue(
          "budgetAmount",
          subAcId ? subAccountBudgetAmount : ""
        );
        formik.setFieldValue("subAcId", subAcId ? subAcId : "");
        formik.setFieldValue("showYearsField", true);
      })
      .catch((err) => {
        console.log(err);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
      });
  };

  useEffect(() => {
    getExpenseCategories();
  }, []);

  useEffect(() => {
    if (
      formik.values.account &&
      formik.values.subAccount &&
      formik.values.year
    ) {
      getBudgetDetailsByYear();
    }
  }, [formik.values.account, formik.values.subAccount, formik.values.year]);

  const handleFieldChange = (field: string, value: any) => {
    formik.setFieldValue(field, value);
    if (field === "account") {
      getExpenseCategoryAccounts(value);
      formik.setFieldValue("subAccount", "");
      formik.setFieldValue("budgetAmount", "");
      formik.setFieldTouched("subAccount", false);
      formik.setFieldTouched("budgetAmount", false);
    } else if (field === "subAccount") {
      formik.setFieldValue("budgetAmount", "");
      formik.setFieldTouched("budgetAmount", false);
    }
  };

  const handleAddBudget = (values: any) => {
    const payload = {
      accountId: values?.subAccount,
      year: values?.year?.toString(),
      budgetAmount: values?.budgetAmount,
    };
    apiRequest({
      method: "POST",
      endpoint: apiPath.addOrUpdateBudget,
      includeToken: true,
      body: payload,
    })
      .then((res) => {
        setBtnLoading(false);
        getBudgetDetailsByYear();
        formik.setTouched({
          budgetAmount: false,
          subAccount: false,
        });
        toast.custom(
          <SuccessPopup
            headerMessage="Add Budget"
            showMessage={"Budget Added Successfully"}
          />
        );
      })
      .catch((err) => {
        setBtnLoading(false);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
      });
  };
  const handleUpdateBudget = (values: any) => {
    const payload = {
      id: values?.subAcId,
      budgetAmount: values?.budgetAmount,
    };
    apiRequest({
      method: "PUT",
      endpoint: `${apiPath.addOrUpdateBudget}/${values?.subAcId}`,
      includeToken: true,
      body: payload,
    })
      .then((res) => {
        formik.setTouched({
          budgetAmount: false,
          subAccount: false,
        });
        setBtnLoading(false);
        toast.custom(
          <SuccessPopup
            headerMessage="Update Budget"
            showMessage={"Updated the Budget Successfully"}
          />
        );
      })
      .catch((err) => {
        setBtnLoading(false);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
      });
  };

  const handleOpenAddSubAccount = () => {
    setOpenAddSubAccountDialog(true);
  };

  const handleAddSubAccount = (subAccount: any) => {
    handleClose();
    dispatch(addExpenseCategoryAccountNumbers([...expenseCategoryAccountNumbers, subAccount]));
    formik.setFieldValue("subAccount", subAccount?.accountId);

  };

  const handleClose = () => {
    setOpenAddSubAccountDialog(false);
  };

  const renderButtonsComponent = () => {
    return (
      <Box mt="10px">
        <CommonButton
          onClick={() => {
            !btnLoading && formik.handleSubmit();
          }}
          sx={{ mr: "10px" }}
        >
          <ButtonLoader
            isBtnLoading={btnLoading}
            btnText={`${formik.values.subAcId ? "Update" : "Add"}`}
          />
        </CommonButton>
      </Box>
    );
  };

  const renderFieldsComponent = () => {
    return (
      <Box className="common_box_shadow" sx={{ mt: "20px" }}>
        <Box
          sx={{ ...groupingStyles.groupedFieldsContainer, flexWrap: "wrap" }}
        >
          <Box sx={groupingStyles.fieldContainer}>
            {CustomRequiredInputLabel("Select Account", true)}
            <CustomSelectField
              handleDropdownChange={handleFieldChange}
              errorMessage={formik.errors.account}
              dropDownList={expenseCategories}
              value={formik.values.account}
              fieldName="account"
              placeholder="Select account"
              optionLabel="description"
              optionValue="accountId"
            />
          </Box>
          <Box
            sx={{ display: "flex", alignItems: "flex-end", flexWrap: "wrap" }}
          >
            {formik.values.showSubAccountField &&
              expenseCategoryAccountNumbers?.length > 0 && (
                <Box sx={groupingStyles.fieldContainer}>
                  {CustomRequiredInputLabel("Select Sub-Account", true)}
                  <CustomSelectField
                    handleDropdownChange={handleFieldChange}
                    errorMessage={
                      formik.touched.subAccount ? formik.errors.subAccount : ""
                    }
                    dropDownList={expenseCategoryAccountNumbers}
                    value={formik.values.subAccount}
                    fieldName="subAccount"
                    placeholder="Select sub-account"
                    optionLabel="description"
                    optionValue="accountId"
                  />
                </Box>
              )}
            {formik.values.showSubAccountField && (
              <CommonButton
                onClick={handleOpenAddSubAccount}
                sx={{ mb: "15px" }}
              >
                <ButtonLoader
                  isBtnLoading={false}
                  btnText={`Add Sub Account`}
                />
              </CommonButton>
            )}
          </Box>
        </Box>
        {formik.values.showYearsField &&
          expenseCategoryAccountNumbers?.length > 0 && (
            <>
              <Box sx={groupingStyles.groupedFieldsContainer}>
                <Box sx={groupingStyles.fieldContainer}>
                  {CustomRequiredInputLabel("Select Year", true)}
                  <CustomSelectField
                    handleDropdownChange={handleFieldChange}
                    errorMessage={formik.errors.year}
                    dropDownList={getCurrentYearAndNextYears()}
                    value={formik.values.year}
                    fieldName="year"
                    placeholder="Select year"
                    optionLabel="year"
                    optionValue="year"
                  />
                </Box>
                <Box sx={groupingStyles.fieldContainer}>
                  {CustomRequiredInputLabel("Budget", true)}
                  <CustomTextFeild
                    fullWidth
                    required
                    name="budgetAmount"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoney
                            fontSize="small"
                            sx={{ position: "absolute" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    value={formik.values.budgetAmount}
                    placeholder="Enter amount"
                    onChange={(e) => {
                      const inputValue = e.target.value.trim(); // Trim whitespace
                      const newValue = inputValue.slice(0, 15);
                      if (newValue === "") {
                        formik.setFieldValue("budgetAmount", "");
                      } else if (/^\d*\.?\d*$/.test(newValue)) {
                        formik.setFieldValue("budgetAmount", newValue);
                      }
                    }}
                    sx={
                      formik.touched.budgetAmount && formik.errors.budgetAmount
                        ? textFieldStyles.errorTextField
                        : textFieldStyles.textField
                    }
                  />
                  {formik.touched.budgetAmount &&
                    formik?.errors?.budgetAmount && (
                      <Typography sx={{ color: "red", fontSize: "12px" }}>
                        {formik?.errors?.budgetAmount}
                      </Typography>
                    )}
                </Box>
              </Box>
              {renderButtonsComponent()}
            </>
          )}
        {formik.values.showSubAccountField &&
          !(expenseCategoryAccountNumbers?.length > 0) && (
            <Typography
              className="sub_heading"
              sx={{ fontSize: "16px !important", color: "#F05802 !important" }}
            >
              No Sub-accounts found.
            </Typography>
          )}
      </Box>
    );
  };
  return (
    <>
      <Toaster />
      <Typography className="sub_heading">Add/Update Budget</Typography>
      {renderFieldsComponent()}
      <CreateSubAccount
        open={openAddSubAccountDialog}
        handleClose={handleClose}
        mainAccount={formik.values.account}
        expenseAccounts={expenseCategories}
        handleAddSubAccount={handleAddSubAccount}
      />
    </>
  );
};

export default AddOrUpdateBudget;
